// /cart/shipping のお届け日が、ブラウザのカード補完で「翌月か翌々月の 1 日」に変わるのを防ぐ（Issue #110）。
//
// お届け日の欄は 2 つある（fukuro.in の本番で実物確認・2026-10-20）:
//   select[name="delivery[N][shippingdate_ymd]"].shipping_date_selection … 送信される本物の値
//   label.date_edit_label > input[type=date][name="calendar_delivery[N][shippingdate_ymd]"]
//     … おちゃのこの /res/dist/select-date-calender.js が足すカレンダー。input が起きると同じ値を select に写す（syncSelectElem）。
//       select の範囲外の日付なら select は「指定なし(最短)」に戻る（エラー表示は datePickerForcused 次第で出ないことがある）
// 同じページにクロネコのカードの有効期限（cc-exp-month / cc-exp-year）があり、補完が日付入力を期限の欄と取り違えて
// 「期限の年-期限の月-01」を入れ、それが select へ写る、と見ている（未実証）。
//
// 1. 日付入力を、お客さまが触るまで readonly にする（補完は readonly の欄に値を入れない）。name も外す
//    （送信されても使われていない欄。名前の date が取り違えの手がかりになっていそうなため）。
//    外すのは pointerdown（label ごと見る。カレンダーのアイコンも label の中）と focus（Tab）。
//    おちゃのこの click → showPicker() は readonly だと例外になるので、click より先に来る pointerdown で外す。
//    select-date-calender.js は要素を参照で持っているので、name を外しても写しは動く。
// 2. 「クレジットカード・10 日以上先・1 日」がそろったら「次へ」の上に警告を出す。止めない。
//
// ★ select（送信される値）には触らない。戻す・選び直す・案内するのはなし（田口さん 10/20）。
// ★ カードの欄には触らない。カード情報を読まない・記録しない。

import { daysFromToday } from './betchu'
import { afterOldScripts } from '../shared/after-old-scripts'

/** 警告を出す下限（今日から何日先から） */
export const WARN_MIN_DAYS = 10
export const CREDIT_CARD_LABEL = 'クレジットカード'
export const WARNING_CLASS = 'wu-delivery-date-warning'

export const warningText = (month: number): string =>
  `お届け日が ${month}月1日 になっています。ご希望の日付でお間違いないか、ご確認ください。`

/** select の値（"YYYY-MM-DD"）が「10 日以上先の 1 日」なら月を返す。それ以外は null */
export function firstOfMonthAhead(value: string, today: Date): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m || m[3] !== '01') return null
  const days = daysFromToday(value, today)
  if (days === null || days < WARN_MIN_DAYS) return null
  return Number(m[2])
}

/** 警告を出す月（重複なし・出てきた順）。カード払いでなければ空 */
export function warnMonths(values: string[], creditCard: boolean, today: Date): number[] {
  if (!creditCard) return []
  const months: number[] = []
  for (const v of values) {
    const month = firstOfMonthAhead(v, today)
    if (month !== null && !months.includes(month)) months.push(month)
  }
  return months
}

/** 選ばれているお支払い方法が「クレジットカード」か。pay_procedure の値は店で違うのでラベルの文字で見る */
export function isCreditCardSelected(doc: Document): boolean {
  const radio = doc.querySelector<HTMLInputElement>('input[name="pay_procedure"]:checked')
  if (!radio) return false
  const label = radio.closest('label') ?? (radio.id ? doc.querySelector(`label[for="${radio.id}"]`) : null)
  return (label?.textContent ?? '').includes(CREDIT_CARD_LABEL)
}

/** カレンダーの日付入力を、触られるまで補完できなくする。触られたら readonly を外す（name は外したまま） */
export function guardCalendarInput(input: HTMLInputElement): void {
  if (input.dataset.wuGuard) return
  input.dataset.wuGuard = '1'
  input.readOnly = true
  input.removeAttribute('name')
  input.setAttribute('autocomplete', 'off')
  const release = (): void => {
    input.readOnly = false
  }
  // label を押すと input に click が転送されるので、label の pointerdown も見る（カレンダーのアイコンは label の中）
  ;(input.closest('label') ?? input).addEventListener('pointerdown', release, true)
  input.addEventListener('pointerdown', release, true)
  input.addEventListener('focus', release, true)
}

const findCalendarInput = (select: HTMLSelectElement): HTMLInputElement | null =>
  select.parentElement?.querySelector<HTMLInputElement>('input[type="date"]') ?? null

function renderWarning(doc: Document, months: number[]): void {
  let box = doc.querySelector<HTMLElement>(`.${WARNING_CLASS}`)
  if (months.length === 0) {
    box?.remove()
    return
  }
  const next = doc.querySelector('.nextinput')
  // 「戻る」「次へ」は .btn_box の中で float で横に並んでいる（fukuro.in で確認）。その中に入れると横に並んでしまうので、
  // 箱ごとの手前（＝「次へ」のすぐ上）に置く
  const anchor = next?.closest('.btn_box') ?? next?.closest('.nextinput_btn') ?? next?.parentElement
  if (!anchor?.parentNode) return
  if (!box) {
    box = doc.createElement('p')
    box.className = WARNING_CLASS
    box.setAttribute('role', 'alert')
    box.style.cssText =
      'margin:0 0 12px;padding:10px 14px;border:2px solid #d32f2f;background:#fff4f4;color:#c62828;font-weight:bold;line-height:1.6;text-align:left;'
  }
  box.textContent = months.map(warningText).join('\n')
  box.style.whiteSpace = 'pre-line'
  if (box.nextSibling !== anchor) anchor.parentNode.insertBefore(box, anchor)
}

export default function cartDeliveryDateGuard(): void {
  const selects = Array.from(document.querySelectorAll<HTMLSelectElement>('.shipping_date_selection'))
  if (selects.length === 0) return

  const guardAll = (): void => {
    for (const s of selects) {
      const input = findCalendarInput(s)
      if (input) guardCalendarInput(input)
    }
  }
  // カレンダーはページ本文の inline script が select の直後で足す（fukuro.in で確認）ので、defer の時点で普通はある。
  // 足すのが遅れる店に備えて、旧スクリプトが走り終わった後にもう一度見る
  guardAll()
  afterOldScripts(guardAll)

  const update = (): void => {
    renderWarning(document, warnMonths(selects.map((s) => s.value), isCreditCardSelected(document), new Date()))
  }
  for (const s of selects) {
    s.addEventListener('input', update)
    s.addEventListener('change', update)
  }
  document.querySelectorAll('input[name="pay_procedure"]').forEach((r) => r.addEventListener('change', update))
  update()
  // 戻る・再読み込みで値が復元されたときや、別注の選択肢の削り（cart-betchu-shipping）の後にも見直す
  afterOldScripts(update)
}
